import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Store, CheckCircle, ArrowRight, Phone, MapPin, DollarSign, Zap, Star, Award, Users, Calendar } from 'lucide-react';
import { useCurrency } from '../contexts/CurrencyContext';

const HighStreetBusinesses = () => {
  const { formatPrice } = useCurrency();

  // Scroll to top helper
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const industries = [
    { name: 'Hair Salon', route: '/industries/hair-salon', icon: '💇' },
    { name: 'Barber Shop', route: '/industries/barber-shop', icon: '✂️' },
    { name: 'Nail Salon', route: '/industries/nail-salon', icon: '💅' },
    { name: 'Tattoo Shop', route: '/industries/tattoo-shop', icon: '🎨' },
    { name: 'Day Spa', route: '/industries/spa-services', icon: '🧖' },
    { name: 'Bike Shop', route: '/industries/bike-shop', icon: '🚴' },
    { name: 'Auto Detailing', route: '/industries/auto-detailing', icon: '🚗' },
    { name: 'Pet Grooming', route: '/industries/pet-grooming', icon: '🐕' },
    { name: 'Florist', route: '/industries/florist', icon: '💐' },
    { name: 'Optometry', route: '/industries/optometry', icon: '👓' }
  ];

  const challenges = [
    { icon: '🚶', title: 'Unpredictable Walk-Ins', desc: 'Chaos from walk-ins during busy times' },
    { icon: '🔄', title: 'Hard to Get Rebookings', desc: 'Customers forget to rebook before leaving' },
    { icon: '💺', title: 'Empty Chairs', desc: 'Revenue lost during slow periods' },
    { icon: '👻', title: 'One-Time Customers', desc: 'Hard to turn walk-ins into regulars' },
    { icon: '📞', title: 'Constant Phone Calls', desc: 'Phone rings non-stop during busy hours' }
  ];

  const solutions = [
    { icon: '📅', title: 'Online Booking 24/7', desc: 'Fill your schedule around the clock' },
    { icon: '🔔', title: 'Auto Rebook Reminders', desc: 'Before they walk out, book their next visit' },
    { icon: '💰', title: 'Dynamic Pricing', desc: 'Special rates automatically for slow times' },
    { icon: '🎁', title: 'Loyalty Automation', desc: 'Reward program runs itself' },
    { icon: '⏰', title: 'SMS Waitlist', desc: 'Turn walk-ins away without losing them' }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#0E163B] via-[#1a2654] to-[#0E163B] text-white py-24 px-4 overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{ 
            backgroundImage: 'radial-gradient(circle at 20px 20px, white 2px, transparent 0)',
            backgroundSize: '40px 40px'
          }}></div>
        </div>

        {/* Decorative Elements */}
        <div className="absolute top-20 right-10 w-72 h-72 bg-[#FF5A3D] rounded-full opacity-10 blur-3xl"></div>
        <div className="absolute bottom-20 left-10 w-96 h-96 bg-[#FF8C42] rounded-full opacity-10 blur-3xl"></div>

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-12">
            {/* Badge */}
            <div className="inline-flex items-center bg-[#FF5A3D]/20 backdrop-blur-sm border border-[#FF5A3D]/30 px-6 py-3 rounded-full mb-8">
              <Store size={20} className="text-[#FF5A3D] mr-2" />
              <span className="text-white font-semibold">10 High Street Industries</span>
            </div>

            <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              High Street & Walk-In<br/>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FF5A3D] to-[#FF8C42]">
                Growth Platform
              </span>
            </h1>
            
            <p className="text-xl md:text-2xl text-gray-300 mb-10 max-w-3xl mx-auto leading-relaxed">
              Turn walk-ins into regulars, fill empty chairs, and build a loyal customer base on autopilot
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12">
              <Button 
                asChild
                className="bg-gradient-to-r from-[#FF5A3D] to-[#FF8C42] hover:from-[#E5512E] hover:to-[#E57A35] text-white font-bold text-lg px-10 py-7 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-2xl"
              >
                <Link to="/pricing" onClick={scrollToTop}>
                  Start {formatPrice(5)} Trial
                  <ArrowRight className="ml-2" size={20} />
                </Link>
              </Button>
              <Button 
                asChild
                variant="outline"
                className="border-2 border-white/30 bg-white/10 backdrop-blur-sm text-white hover:bg-white hover:text-[#0E163B] font-semibold text-lg px-10 py-7 rounded-xl transition-all duration-300"
              >
                <Link to="/contact" onClick={scrollToTop}>
                  Talk to Expert
                </Link>
              </Button>
            </div>

            {/* Trust Indicators */}
            <div className="flex flex-wrap justify-center gap-8 text-sm text-gray-300">
              <div className="flex items-center">
                <CheckCircle size={18} className="text-green-400 mr-2" />
                <span>Setup in 5 Minutes</span>
              </div>
              <div className="flex items-center">
                <CheckCircle size={18} className="text-green-400 mr-2" />
                <span>No Long-Term Contract</span>
              </div>
              <div className="flex items-center">
                <CheckCircle size={18} className="text-green-400 mr-2" />
                <span>Cancel Anytime</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Challenges */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-block bg-red-100 text-red-600 px-4 py-2 rounded-full text-sm font-semibold mb-4">
              COMMON PROBLEMS
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-[#0E163B] mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Challenges Holding You Back
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Sound familiar? These are the daily struggles walk-in businesses face
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {challenges.map((challenge, index) => (
              <Card key={index} className="border-2 border-gray-200 hover:border-[#FF5A3D] hover:shadow-lg transition-all duration-300 group">
                <CardContent className="p-8">
                  <div className="text-5xl mb-4 group-hover:scale-110 transition-transform duration-300">{challenge.icon}</div>
                  <h3 className="text-lg font-bold text-[#0E163B] mb-2">{challenge.title}</h3>
                  <p className="text-gray-600">{challenge.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Solutions */}
      <section className="py-20 px-4 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-block bg-green-100 text-green-600 px-4 py-2 rounded-full text-sm font-semibold mb-4">
              OUR SOLUTIONS
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-[#0E163B] mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              How We Solve These Problems
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Powerful automation that transforms how you run your business
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6">
            {solutions.map((solution, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="w-14 h-14 bg-gradient-to-br from-[#FF5A3D] to-[#FF8C42] rounded-xl flex items-center justify-center text-2xl shadow-lg">
                      {solution.icon}
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-[#0E163B] mb-2">{solution.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{solution.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-block bg-[#FF5A3D]/10 text-[#FF5A3D] px-4 py-2 rounded-full text-sm font-semibold mb-4">
              CHOOSE YOUR INDUSTRY
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-[#0E163B] mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              10 High Street Industries
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Pre-built snapshots ready for your specific business
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {industries.map((industry, index) => (
              <Link key={index} to={industry.route} onClick={scrollToTop}>
                <div className="bg-gradient-to-br from-white to-gray-50 border-2 border-gray-200 hover:border-[#FF5A3D] rounded-xl p-6 transition-all duration-300 transform hover:-translate-y-1 hover:shadow-xl cursor-pointer group">
                  <div className="text-4xl mb-3 group-hover:scale-110 transition-transform duration-300">{industry.icon}</div>
                  <h3 className="text-base font-bold text-[#0E163B] group-hover:text-[#FF5A3D] transition-colors">
                    {industry.name}
                  </h3>
                  <div className="flex items-center text-[#FF5A3D] font-semibold text-sm mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    View <ArrowRight size={14} className="ml-1" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-20 px-4 bg-gradient-to-br from-[#0E163B] via-[#1a2654] to-[#0E163B] text-white relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{ 
            backgroundImage: 'radial-gradient(circle at 20px 20px, white 2px, transparent 0)',
            backgroundSize: '40px 40px'
          }}></div>
        </div>

        <div className="max-w-6xl mx-auto relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Average Results for Walk-In Businesses
            </h2>
            <p className="text-xl text-gray-300">
              Real metrics from businesses using our platform
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-8 text-center hover:bg-white/15 transition-all duration-300">
              <div className="bg-gradient-to-br from-[#FF5A3D] to-[#FF8C42] w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-xl">
                <Calendar size={40} className="text-white" />
              </div>
              <div className="text-5xl font-bold text-white mb-2">85%</div>
              <div className="text-xl text-gray-300">Rebook Before Leaving</div>
              <p className="text-sm text-gray-400 mt-2">Turn one-time visits into loyal customers</p>
            </div>
            
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-8 text-center hover:bg-white/15 transition-all duration-300">
              <div className="bg-gradient-to-br from-[#FF5A3D] to-[#FF8C42] w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-xl">
                <Users size={40} className="text-white" />
              </div>
              <div className="text-5xl font-bold text-white mb-2">40%</div>
              <div className="text-xl text-gray-300">Increase in Bookings</div>
              <p className="text-sm text-gray-400 mt-2">Fill more appointments automatically</p>
            </div>
            
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-8 text-center hover:bg-white/15 transition-all duration-300">
              <div className="bg-gradient-to-br from-[#FF5A3D] to-[#FF8C42] w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-xl">
                <DollarSign size={40} className="text-white" />
              </div>
              <div className="text-5xl font-bold text-white mb-2">60%</div>
              <div className="text-xl text-gray-300">Join Loyalty Program</div>
              <p className="text-sm text-gray-400 mt-2">Build predictable repeat business</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-4 bg-gradient-to-r from-[#FF5A3D] via-[#FF6B47] to-[#FF8C42] text-white relative overflow-hidden">
        {/* Decorative Elements */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-white/10 rounded-full translate-y-1/2 -translate-x-1/2"></div>

        <div className="max-w-4xl mx-auto text-center relative z-10">
          <Award size={64} className="mx-auto mb-6" />
          <h2 className="text-4xl md:text-6xl font-bold mb-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            Ready to Dominate Your Local Market?
          </h2>
          <p className="text-xl md:text-2xl mb-10 opacity-95 leading-relaxed">
            Join hundreds of successful high street businesses using Local Business Heroes
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <Button 
              asChild
              className="bg-white text-[#FF5A3D] hover:bg-gray-100 font-bold text-xl px-12 py-7 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-2xl"
            >
              <Link to="/pricing" onClick={scrollToTop}>
                Start 14-Day Trial for {formatPrice(5)}
              </Link>
            </Button>
          </div>

          <div className="flex flex-wrap justify-center gap-6 text-sm">
            <div className="flex items-center bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full">
              <Star size={16} className="mr-2" />
              <span>Setup in 5 Minutes</span>
            </div>
            <div className="flex items-center bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full">
              <CheckCircle size={16} className="mr-2" />
              <span>Credit Card Required</span>
            </div>
            <div className="flex items-center bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full">
              <Zap size={16} className="mr-2" />
              <span>Cancel Anytime</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HighStreetBusinesses;
